import { useState, useTransition, type ReactNode } from "react";
import { useNavigate as useRouter } from "../../router";
import { Badge } from "../../badge";
import { Button } from "../../button";
import { Input } from "../../input";
import { SettingsSection } from "./settings-section";

export interface ReferenceRow {
  id: number;
  slug: string;
  name: string;
}

export interface ReferenceFlag {
  /** Row field name (e.g. "is_terminal"). Value is treated as truthy=on. */
  key: string;
  /** Display label (e.g. "terminal"). */
  label: string;
}

export type ReferenceFlagValues = Record<string, boolean>;

export interface ReferenceListEditorProps<T extends ReferenceRow> {
  title: ReactNode;
  description?: ReactNode;
  rows: T[];
  /** Optional boolean columns rendered as toggle pills. */
  flags?: ReferenceFlag[];
  /** Noun shown in empty state + add row (e.g. "status", "source"). */
  itemNoun?: string;
  onCreate: (input: {
    slug: string;
    name: string;
    flags: ReferenceFlagValues;
  }) => Promise<void>;
  /** Optional. When omitted, name renders as plain text (no inline edit)
   *  and flag toggles are disabled. */
  onUpdate?: (
    id: number,
    patch: { name?: string; flags?: ReferenceFlagValues },
  ) => Promise<void>;
  onArchive: (id: number) => Promise<void>;
}

function readFlags(row: ReferenceRow, flags: ReferenceFlag[]): ReferenceFlagValues {
  const out: ReferenceFlagValues = {};
  for (const f of flags) {
    out[f.key] = Boolean((row as unknown as Record<string, unknown>)[f.key]);
  }
  return out;
}

export function ReferenceListEditor<T extends ReferenceRow>({
  title,
  description,
  rows,
  flags = [],
  itemNoun = "item",
  onCreate,
  onUpdate,
  onArchive,
}: ReferenceListEditorProps<T>) {
  const router = useRouter();
  const [pending, startTransition] = useTransition();
  const [error, setError] = useState<string | null>(null);
  const [draft, setDraft] = useState<{
    slug: string;
    name: string;
    flags: ReferenceFlagValues;
  }>(() => ({
    slug: "",
    name: "",
    flags: Object.fromEntries(flags.map((f) => [f.key, false])),
  }));

  function run<T>(fn: () => Promise<T>) {
    setError(null);
    startTransition(async () => {
      try {
        await fn();
        router.refresh();
      } catch (err) {
        setError(err instanceof Error ? err.message : "Action failed.");
      }
    });
  }

  function toggleFlag(rowId: number, key: string, current: boolean) {
    if (!onUpdate) return;
    run(() => onUpdate(rowId, { flags: { [key]: !current } }));
  }

  function commitName(row: T, value: string) {
    if (!onUpdate || value === row.name) return;
    run(() => onUpdate(row.id, { name: value }));
  }

  return (
    <SettingsSection title={title} description={description}>
      {rows.length === 0 ? (
        <div className="type-body text-muted-foreground">
          No {itemNoun}s yet. Add one below.
        </div>
      ) : (
        <ul className="flex flex-col">
          {rows.map((row) => (
            <li
              key={row.id}
              className="flex items-center gap-2 border-b border-border/40 py-2 first:pt-0 last:border-b-0 last:pb-0"
            >
              {onUpdate ? (
                <Input
                  defaultValue={row.name}
                  className="max-w-56"
                  onBlur={(e) => commitName(row, e.target.value)}
                />
              ) : (
                <span className="type-body text-foreground">{row.name}</span>
              )}
              <span className="type-body text-muted-foreground">/{row.slug}</span>
              <div className="flex flex-1 items-center gap-1">
                {flags.map((f) => {
                  const on = Boolean((row as unknown as Record<string, unknown>)[f.key]);
                  return (
                    <button
                      key={f.key}
                      type="button"
                      disabled={pending || !onUpdate}
                      onClick={() => toggleFlag(row.id, f.key, on)}
                      aria-pressed={on}
                    >
                      <Badge variant={on ? "default" : "outline"}>
                        {f.label}
                      </Badge>
                    </button>
                  );
                })}
              </div>
              <Button
                size="xs"
                variant="ghost"
                disabled={pending}
                onClick={() => run(() => onArchive(row.id))}
              >
                Remove
              </Button>
            </li>
          ))}
        </ul>
      )}

      <div className="mt-4 grid grid-cols-[1fr_1fr_auto] gap-2 items-end">
        <Input
          placeholder={`New ${itemNoun} name`}
          value={draft.name}
          onChange={(e) => setDraft({ ...draft, name: e.target.value })}
        />
        <Input
          placeholder="slug"
          value={draft.slug}
          onChange={(e) => setDraft({ ...draft, slug: e.target.value })}
        />
        <Button
          size="sm"
          disabled={pending || !draft.name || !draft.slug}
          onClick={() => {
            const snapshot = draft;
            run(async () => {
              await onCreate(snapshot);
              setDraft({
                slug: "",
                name: "",
                flags: Object.fromEntries(flags.map((f) => [f.key, false])),
              });
            });
          }}
        >
          Add {itemNoun}
        </Button>
      </div>

      {error ? <p className="mt-2 type-body text-destructive">{error}</p> : null}
    </SettingsSection>
  );
}
